import React, { createContext, useState, useContext, useCallback } from 'react';
import useWebSocket from 'react-use-websocket';
import useSessionStorage from '../hooks/useSessionStorage';
import * as PwaStatus from './PwaStatus.js';
import * as WebSocketStatus from './WebSocketStatus';
import * as TestStatus from '../components/assessment/TestStatus.js';
import * as Storage from '../components/common/Storage.js';
import { AssessmentContext } from './AssessmentContext.js';

export const WebSocketContext = createContext();

// Max byte size of a single info payload before it is split into chunks.
// Kept under AWS API Gateway's 128 KB WebSocket frame limit.
const MAX_INFO_CHUNK_BYTES = 90 * 1024;

/**
 * Splits a string into pieces that are each at most maxBytes when UTF-8
 * encoded, without breaking multi-byte characters across chunk boundaries.
 * @param {string} str
 * @param {number} maxBytes
 * @returns {string[]}
 */
const chunkStringByBytes = (str, maxBytes) => {
  const encoder = new TextEncoder();
  const chunks = [];
  let current = '';
  let currentBytes = 0;
  for (const char of str) {
    const charBytes = encoder.encode(char).length;
    if (currentBytes + charBytes > maxBytes && current) {
      chunks.push(current);
      current = '';
      currentBytes = 0;
    }
    current += char;
    currentBytes += charBytes;
  }
  if (current) {
    chunks.push(current);
  }
  return chunks;
};

/**
 * Generates a unique id used to correlate chunks of a single error payload.
 * @returns {string}
 */
const generateChunkId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 *
 * @param {string} props.wssURL URL for the websocket
 * @param {string} props.authToken The authentication token to be passed for websocket connection
 * @returns
 */
export const WebSocketProvider = ({ wssURL, authToken, children }) => {
  const { getStorageItem, setStorageItem, removeStorageItem } =
    useSessionStorage();
  const { testStatus, saveState, saveErrorCount } =
    useContext(AssessmentContext);
  const [socketStatus, setSocketStatus] = useState(WebSocketStatus.INITIAL);
  const [attemptNumber, setAttemptNumber] = useState(0);
  const attemptNumBeforeReload = getStorageItem(
    Storage.STORAGE_ATTEMPT_NO_BEFORE_RELOAD
  );
  const maxReconnectAttempts = parseInt(
    window.env.REACT_APP_WS_RECONNECT_ATTEMPTS
  );
  const maxReconnectAttemptsBeforeAuthOrReconnecting = parseInt(
    window.env.REACT_APP_WS_ATTEMPT_BEFORE_RECONNECTING_STATUS
  );

  let reconnectAttemptNos = [
    TestStatus.TEST_AWAITING_LAUNCH, //treat like test awaiting auth - this is just an intermediate test status, needed to reconnect
    TestStatus.TEST_AWAITING_AUTHORIZATION,
    TestStatus.TEST_AUTHENTICATED,
  ].includes(testStatus)
    ? maxReconnectAttemptsBeforeAuthOrReconnecting
    : maxReconnectAttempts;

  const reconnectAttempts = Math.max(
    reconnectAttemptNos - attemptNumBeforeReload,
    0
  );

  window.onbeforeunload = () => {
    setStorageItem(Storage.STORAGE_ATTEMPT_NO_BEFORE_RELOAD, attemptNumber);
  };

  //Heartbeat ping message
  const heartbeatMsg = {
    action: 'nmcHb',
    message: 'PING',
  };

  const updateAttemptNumber = (value) => {
    setAttemptNumber(value);
    if (value === 0) {
      removeStorageItem(Storage.STORAGE_ATTEMPT_NO);
      removeStorageItem(Storage.STORAGE_ATTEMPT_NO_BEFORE_RELOAD);
    } else {
      setStorageItem(Storage.STORAGE_ATTEMPT_NO, value);
    }
  };

  const getPWAState = useCallback(() => {
    let pwaState;
    switch (testStatus) {
      case TestStatus.TEST_LAUNCHED:
      case TestStatus.TEST_INSESSION:
      case TestStatus.TEST_PAUSED:
        if (
          saveState === TestStatus.SAVE_ERROR &&
          saveErrorCount >= TestStatus.MIN_CONSECUTIVE_ERRORS
        ) {
          pwaState = PwaStatus.LEARNOSITY_DOWN;
        } else {
          //saveState is success or saveStatus is error and error count is between 0 - 2
          pwaState = PwaStatus.LEARNOSITY_UP;
        }
        break;
      case TestStatus.TEST_AWAITING_AUTHORIZATION:
      case TestStatus.TEST_AWAITING_LAUNCH://treat like test awaiting auth - this is just an intermediate test status
        pwaState = PwaStatus.AWAITING_AUTH;
        break;
      default:
        pwaState = PwaStatus.INITIAL;
        break;
    }
    return pwaState;
  }, [testStatus, saveState, saveErrorCount]);

  const getSocketUrl = useCallback(() => {
    //Introduce a 3 seconds delay on websocket connection after login or on reload
    //to make sure disconnect lambda has enough time to complete execution.
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(wssURL);
      }, 3000);
    });
  }, [wssURL]);

  //Websocket configuration
  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    getSocketUrl,
    {
      shouldReconnect: (closeEvent) => {
        console.log(
          `Websocket Event: shouldReconnect, Code: ${closeEvent.code} , Reason: ${closeEvent.reason}`
        );
        let lastRecordedWakeTime = getStorageItem(Storage.STORAGE_LAST_RECORDED_WAKE_TIME);
        return !lastRecordedWakeTime;
      },
      //TODO need additional logic on what happens when the socket connection fails during test
      reconnectAttempts: reconnectAttempts,
      reconnectInterval: (__attemptNumber) => {
        const fromStorage = getStorageItem('attemptNumber');
        var savedAttemptNumber = __attemptNumber;
        if (fromStorage) {
          savedAttemptNumber = parseInt(fromStorage) + 1;
        }
        console.log(`Reconnecting... attemptNumber: ${savedAttemptNumber}`);
        updateAttemptNumber(savedAttemptNumber);
        if (
          savedAttemptNumber >= maxReconnectAttemptsBeforeAuthOrReconnecting &&
          socketStatus !== WebSocketStatus.INITIAL
        ) {
          setSocketStatus(WebSocketStatus.RECONNECTING);
        }
        return parseInt(window.env.REACT_APP_WS_RECONNECT_INTERVAL_MS); //30s in milliseconds
      },
      heartbeat: {
        message: JSON.stringify(heartbeatMsg),
        returnMessage: 'ACK',
        timeout: parseInt(window.env.REACT_APP_WS_HEARTBEAT_TIMEOUT_MS), //1min in milliseconds, needs to more than interval - maybe at least 2x
        interval: parseInt(window.env.REACT_APP_WS_HEARTBEAT_INTERVAL_MS), //30s in milliseconds
      },
      protocols: [authToken, getPWAState()],
      onClose: (closeEvent) => {
        console.log(
          `Websocket Event: onClose, Code: ${closeEvent.code} , Reason: ${closeEvent.reason}`
        );
      },
      onError: (error) => {
        console.log(`Websocket Event: onError - ${JSON.stringify(error)}`);
      },
      onOpen: (openEvent) => {
        console.log(
          `Websocket Event: onOpen: connected : ${JSON.stringify(openEvent)}`
        );
        setSocketStatus(WebSocketStatus.OPEN);
        updateAttemptNumber(0);
      },
      onReconnectStop: (numAttempted) => {
        console.log(
          'Websocket Event: onReconnectStop, numAttempted:' + numAttempted
        );
        //TODO may be we so not set socket fail to true once auth is approved (??)
        updateAttemptNumber(0);
        if (socketStatus === WebSocketStatus.INITIAL) {
          setSocketStatus(WebSocketStatus.FAILED_TO_CONNECT);
        } else {
          setSocketStatus(WebSocketStatus.FAILED_TO_RECONNECT);
        }
      },
    }
  );

  /**
   * Uses Websockets sendJsonMessage method to send message to web socket.
   * @param {*} msg If isTestEvent msg is a string otherwise it is an object
   * @param {*} isTestEvent Boolean that indicate if the message is related to test event
   */
  const sendMsgToWebsocket = useCallback(
    (msg, isTestEvent = true, error, info) => {
      console.log(
        `Sending websocket message... Message - ${
          isTestEvent ? JSON.stringify(msg) : JSON.stringify(msg.event)
        } ${error ? `Error - ${JSON.stringify(error)}` : ''}`,
      );
      if (isTestEvent) {
        // The info field can be a large object. If its serialized size exceeds
        // a single WebSocket frame, split it into multiple messages that share
        // a chunkId so the receiver can reassemble it. error is always sent
        // inline.
        const serializedInfo =
          info !== undefined && info !== null
            ? typeof info === 'string'
              ? info
              : JSON.stringify(info)
            : undefined;
        const infoBytes = serializedInfo
          ? new TextEncoder().encode(serializedInfo).length
          : 0;

        if (serializedInfo && infoBytes > MAX_INFO_CHUNK_BYTES) {
          const infoChunks = chunkStringByBytes(
            serializedInfo,
            MAX_INFO_CHUNK_BYTES
          );
          const chunkId = generateChunkId();
          const totalChunks = infoChunks.length;
          // error rides along on the first chunk only, so the receiver doesn't
          // get duplicates and later chunks stay within the frame limit.
          infoChunks.forEach((infoChunk, chunkIndex) => {
            sendJsonMessage({
              action: 'nmcClientMsg',
              message: {
                msg_type: 'TEST_EVENT',
                msg: {
                  clientTimestamp: new Date().toISOString(),
                  event_type: msg,
                  ...(info
                    ? {
                        info: {
                          chunk: infoChunk,
                          chunkId: chunkId,
                          chunkIndex: chunkIndex,
                          totalChunks: totalChunks,
                        },
                      }
                    : {}),
                  ...(chunkIndex === 0 && error ? { error: error } : {}),
                },
              },
            });
          });
        } else {
          sendJsonMessage({
            action: 'nmcClientMsg',
            message: {
              msg_type: 'TEST_EVENT',
              msg: {
                clientTimestamp: new Date().toISOString(),
                event_type: msg,
                ...(error ? { error: error } : {}),
                ...(info ? { info: info } : {}),
              },
            },
          });
        }
      } else {
        sendJsonMessage({
          action: 'nmcClientEvents',
          message: msg,
        });
      }
    },
    [sendJsonMessage]
  );

  return (
    <WebSocketContext.Provider
      value={{
        wssURL,
        socketStatus,
        readyState,
        sendMsgToWebsocket,
        lastJsonMessage,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
